/**
 * Shared style utilities for the style selection grid and style guide modal.
 * The STYLES array (with translated labels/descriptions) stays inside each
 * component that uses useTranslation(). This module only exports pure helpers
 * that do not require i18n context.
 */

/** Canonical ordered list of active style IDs — single source of truth. */
export const STYLE_IDS = [
  'CONTEXTO_USO',
  'EDITORIAL_LUXO',
  'LIFESTYLE_MINIMAL',
  'STUDIO_PROFISSIONAL',
] as const;

export type StyleId = typeof STYLE_IDS[number];

/** Style IDs that were removed but may appear in historical generation data. */
export const LEGACY_STYLE_IDS = [
  'FUNDO_BRANCO_MARKETPLACE',
  'CLOSE_UP_DETALHE',
  'AMBIENTE_NATURAL',
  'FLAT_LAY',
  'MINIMALISTA_SOMBRAS',
] as const;

export function isLegacyStyle(styleId: string): boolean {
  return (LEGACY_STYLE_IDS as readonly string[]).includes(styleId);
}

/** Maps a style ID to its public/style-examples/ filename prefix. */
export function getStyleFilename(styleId: string): string {
  const map: Record<string, string> = {
    CONTEXTO_USO: 'contexto-uso',
    EDITORIAL_LUXO: 'editorial-luxo',
    LIFESTYLE_MINIMAL: 'lifestyle-minimal',
    STUDIO_PROFISSIONAL: 'studio-profissional',
  };
  return map[styleId] ?? styleId.toLowerCase().replace(/_/g, '-');
}

/**
 * Returns the file extension for style example images.
 * LIFESTYLE_MINIMAL uses .jpeg; all others use .png.
 */
export function getStyleExt(styleId: string): string {
  return styleId === 'LIFESTYLE_MINIMAL' ? 'jpeg' : 'png';
}

/**
 * Returns the URL for the generic "before" product photo placeholder.
 */
export function getBeforeImageUrl(cacheVersion = 'v2'): string {
  return `/style-examples/before-generic.png?v=${cacheVersion}`;
}

/**
 * Builds the array of example image objects for a given style, ready
 * to pass as the `images` prop to ImageCarouselModal.
 *
 * Each entry includes both the styled output (`imageUrl`) and the
 * generic before photo (`beforeUrl`) for before/after comparison.
 * The `imageUrl` field is kept for backward compatibility with
 * ImageCarouselModal.
 */
export function getStyleExampleImages(
  styleId: string,
  cacheVersion = 'v2'
): Array<{ id: string; imageUrl: string; beforeUrl: string }> {
  const prefix = getStyleFilename(styleId);
  const ext = getStyleExt(styleId);
  const beforeUrl = getBeforeImageUrl(cacheVersion);
  return [1, 2, 3].map((num) => ({
    id: `${styleId}-${num}`,
    imageUrl: `/style-examples/${prefix}-${num}.${ext}?v=${cacheVersion}`,
    beforeUrl,
  }));
}
